import React from 'react';
import {
  Button,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Tooltip,
  makeStyles,
  shorthands,
  tokens
} from '@fluentui/react-components';
import {
  WeatherSunny24Regular,
  WeatherMoon24Regular,
  AutoFitWidth24Regular,
  WeatherSunny20Regular,
  WeatherMoon20Regular,
  AutoFitWidth20Regular
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useTheme, type ThemeMode } from '../contexts/ThemeContext';

interface ThemeToggleProps {
  variant?: 'icon' | 'text' | 'menu' | 'segmented';
  showLabel?: boolean;
}

const useSegmentedStyles = makeStyles({
  group: {
    display: 'inline-flex',
    alignItems: 'center',
    ...shorthands.gap('2px'),
    ...shorthands.padding('2px'),
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  segment: {
    minWidth: '28px',
    height: '28px',
    ...shorthands.padding(0),
    color: tokens.colorNeutralForeground3,
  },
  segmentActive: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    boxShadow: tokens.shadow2,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1,
    },
  },
});

/**
 * Theme toggle component
 * 
 * Allows users to switch between light, dark, and auto themes
 * Auto mode follows system/Teams theme preferences
 */
export function ThemeToggle({ variant = 'icon', showLabel = false }: ThemeToggleProps) {
  const { themeMode, effectiveMode, setThemeMode, themeSource, isTeamsContext } = useTheme();
  const { t } = useTranslation();

  const getIcon = () => {
    if (themeMode === 'auto') {
      return <AutoFitWidth24Regular />;
    }
    return effectiveMode === 'dark' ? <WeatherMoon24Regular /> : <WeatherSunny24Regular />;
  };

  const getLabel = () => {
    if (themeMode === 'auto') {
      const source = isTeamsContext ? t('theme.sourceTeams') : t('theme.sourceSystem');
      return t('theme.autoWithSource', { source, mode: effectiveMode });
    }
    return themeMode === 'dark' ? t('theme.dark') : t('theme.light');
  };

  const getTooltip = () => {
    if (themeMode === 'auto') {
      const source = isTeamsContext ? t('theme.sourceTeams') : t('theme.sourceSystem');
      return t('theme.themeAuto', { source, mode: effectiveMode });
    }
    return t('theme.themeWithMode', { mode: getLabel() });
  };

  if (variant === 'segmented') {
    return <SegmentedThemeToggle />;
  }

  if (variant === 'menu') {
    return (
      <Menu>
        <MenuTrigger disableButtonEnhancement>
          <Button
            appearance="subtle"
            icon={getIcon()}
            style={{ minWidth: showLabel ? '120px' : undefined }}
          >
            {showLabel && getLabel()}
          </Button>
        </MenuTrigger>
        <MenuPopover>
          <MenuList>
            <MenuItem
              icon={<WeatherSunny24Regular />}
              onClick={() => setThemeMode('light')}
              style={{
                backgroundColor: themeMode === 'light' ? tokens.colorNeutralBackground1Selected : undefined
              }}
            >
              {t('theme.light')}
            </MenuItem>
            <MenuItem
              icon={<WeatherMoon24Regular />}
              onClick={() => setThemeMode('dark')}
              style={{
                backgroundColor: themeMode === 'dark' ? tokens.colorNeutralBackground1Selected : undefined
              }}
            >
              {t('theme.dark')}
            </MenuItem>
            <MenuItem
              icon={<AutoFitWidth24Regular />}
              onClick={() => setThemeMode('auto')}
              style={{
                backgroundColor: themeMode === 'auto' ? tokens.colorNeutralBackground1Selected : undefined
              }}
            >
              {t('theme.auto')} {isTeamsContext ? `(${t('theme.sourceTeams')})` : `(${t('theme.sourceSystem')})`}
              {themeMode === 'auto' && (
                <span style={{ 
                  marginLeft: tokens.spacingHorizontalS, 
                  color: tokens.colorNeutralForeground3 
                }}>
                  • {effectiveMode}
                </span>
              )}
            </MenuItem>
          </MenuList>
        </MenuPopover>
      </Menu>
    );
  }

  if (variant === 'text') {
    return (
      <Button
        appearance="subtle"
        icon={getIcon()}
        onClick={() => {
          // Cycle: auto → light → dark → auto
          if (themeMode === 'auto') setThemeMode('light');
          else if (themeMode === 'light') setThemeMode('dark');
          else setThemeMode('auto');
        }}
      >
        {getLabel()}
      </Button>
    );
  }

  // Default: icon only
  return (
    <Tooltip content={getTooltip()} relationship="label">
      <Button
        appearance="subtle"
        icon={getIcon()}
        onClick={() => {
          // Cycle: auto → light → dark → auto
          if (themeMode === 'auto') setThemeMode('light');
          else if (themeMode === 'light') setThemeMode('dark');
          else setThemeMode('auto');
        }}
        aria-label={getTooltip()}
      />
    </Tooltip>
  );
}

/**
 * Segmented theme selector — three grouped icon buttons (System / Light / Dark),
 * matching the compact settings-row pattern. Each segment has a tooltip and the
 * active mode is highlighted.
 */
function SegmentedThemeToggle() {
  const styles = useSegmentedStyles();
  const { themeMode, setThemeMode, isTeamsContext } = useTheme();
  const { t } = useTranslation();

  const source = isTeamsContext ? t('theme.sourceTeams') : t('theme.sourceSystem');

  const segments: Array<{
    mode: ThemeMode;
    icon: React.ReactElement;
    tooltip: string;
    label: string;
  }> = [
    {
      mode: 'auto',
      icon: <AutoFitWidth20Regular />,
      tooltip: t('theme.selectSystem', { source }),
      label: t('theme.system'),
    },
    {
      mode: 'light',
      icon: <WeatherSunny20Regular />,
      tooltip: t('theme.selectLight'),
      label: t('theme.light'),
    },
    {
      mode: 'dark',
      icon: <WeatherMoon20Regular />,
      tooltip: t('theme.selectDark'),
      label: t('theme.dark'),
    },
  ];

  return (
    <div className={styles.group} role="group" aria-label={t('theme.selectorLabel')}>
      {segments.map((segment) => {
        const active = themeMode === segment.mode;
        return (
          <Tooltip key={segment.mode} content={segment.tooltip} relationship="label" withArrow>
            <Button
              appearance="subtle"
              size="small"
              icon={segment.icon}
              className={`${styles.segment} ${active ? styles.segmentActive : ''}`}
              aria-pressed={active}
              aria-label={segment.label}
              onClick={() => setThemeMode(segment.mode)}
            />
          </Tooltip>
        );
      })}
    </div>
  );
}

/**
 * Theme indicator badge (shows current effective theme)
 */
export function ThemeIndicator() {
  const { t } = useTranslation();
  const { effectiveMode, themeMode, themeSource, isTeamsContext } = useTheme();

  const getSourceLabel = () => {
    if (themeMode !== 'auto') return themeMode;
    return isTeamsContext ? t('theme.sourceTeams') : t('theme.sourceSystem');
  };

  return (
    <div style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: tokens.spacingHorizontalXS,
      padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
      backgroundColor: tokens.colorNeutralBackground2,
      borderRadius: tokens.borderRadiusMedium,
      fontSize: tokens.fontSizeBase200,
      color: tokens.colorNeutralForeground2
    }}>
      {effectiveMode === 'dark' ? <WeatherMoon24Regular /> : <WeatherSunny24Regular />}
      <span>{effectiveMode}</span>
      {themeMode === 'auto' && (
        <span style={{ color: tokens.colorNeutralForeground3 }}>
          ({getSourceLabel()})
        </span>
      )}
    </div>
  );
}
